/**
 * "Show this report as if I were signed in as …".
 *
 * The one filter the console could not do at all, and the one an agent asks for
 * most: a Master wants last week's P&L for ONE of their Super Masters without
 * signing in as them and without exporting three reports and adding them up.
 *
 * Two design decisions are load-bearing:
 *
 *  1. **Agents only.** A Member has no downline, so "read the report as this
 *     member" is not a scope — it is the Member page. The alternative, greying
 *     out every leaf, would put hundreds of dead rows in a 420px panel and make
 *     the agents impossible to find. One line under the tree says where members
 *     went instead.
 *  2. **The whole agent subtree is loaded once, not expanded lazily.** Excluding
 *     members bounds the set to tens of rows, and having it all client-side is
 *     what makes the filter box honest: a lazily-expanded tree can only search
 *     the branches you already opened, which is the worst possible behaviour for
 *     a control whose entire job is "find me that agent".
 *
 * The tree is a FLAT `role="tree"` — every row carries `aria-level`,
 * `aria-posinset` and `aria-setsize` rather than being wrapped in nested
 * `role="group"` lists. Both are valid ARIA; flat keeps one visible-row array
 * as the single source of truth for the cursor, and a cursor that disagrees
 * with the DOM is how tree keyboard support usually breaks.
 */

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import {
  Breadcrumb,
  FilterChip,
  Icon,
  Popover,
  RoleChip,
  SearchInput,
  Spinner,
  type Crumb,
} from '@w7/ui'
import { useReportHierarchyTree } from '@/api/hooks/reports/useReportScope'
import { count } from '@/lib/format'
import { ROLES, ROLE_LABEL, roleDepth, levelToRole, type Role } from '@/features/hierarchy/hierarchy'
import { isAgent, type ReportAccount } from '@/features/reports/types'
import { idsEqual, treeStartParentId } from '@/features/hierarchy/hierarchy'

/** Agent tiers only — see the header note on why members are absent. */
const AGENT_ROLES: Role[] = ROLES.filter(isAgent)

/** How long a type-ahead buffer stays open. The ARIA practices figure. */
const TYPEAHEAD_MS = 800

interface Row {
  account: ReportAccount
  depth: number
  hasChildren: boolean
  expanded: boolean
  /** 1-based position among its siblings, and how many siblings there are. */
  posinset: number
  setsize: number
}

export interface HierarchyScopePickerProps {
  /** The picked account id, or null for your own seat. */
  value: string | null
  onChange: (next: string | null) => void
  /** The signed-in account — always the root of the tree and the default pick. */
  self: ReportAccount | undefined
  /** The picked account, once resolved. Names the trigger without a second read. */
  scoped: ReportAccount | null | undefined
}

export function HierarchyScopePicker({ value, onChange, self, scoped }: HierarchyScopePickerProps) {
  const anchor = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)

  const showing = value === null ? self : (scoped ?? undefined)

  return (
    <>
      <button
        ref={anchor}
        type="button"
        className="w7-rangetrigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Hierarchy scope: ${showing ? showing.username : 'your own seat'}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="downline" size={16} />
        <span className="w7-rangetrigger-text">
          <span className="w7-rangetrigger-preset">Viewing as</span>
          <span className="w7-rangetrigger-sub">
            {showing
              ? `${value === null ? 'You · ' : ''}${showing.username} · ${ROLE_LABEL[showing.role]}`
              : 'Loading'}
          </span>
        </span>
        <Icon name="chevron-down" size={14} />
      </button>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        label="Hierarchy scope"
        width={420}
      >
        {/* Mounted only while open, so opening the report costs no extra read. */}
        <ScopePanel
          value={value}
          self={self}
          onPick={(id) => {
            onChange(id)
            setOpen(false)
          }}
        />
      </Popover>
    </>
  )
}

function ScopePanel({
  value,
  self,
  onPick,
}: {
  value: string | null
  self: ReportAccount | undefined
  onPick: (id: string | null) => void
}) {
  const treeQuery = useReportHierarchyTree()
  const isLoading = treeQuery.isLoading
  const [needle, setNeedle] = useState('')
  const [tiers, setTiers] = useState<Role[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [cursor, setCursor] = useState<string | null>(value ?? self?.id ?? null)
  const rowRefs = useRef(new Map<string, HTMLLIElement>())
  const typeahead = useRef({ text: '', at: 0 })

  const index = useMemo(() => {
    const byId = new Map<string, ReportAccount>()
    const children = new Map<string, ReportAccount[]>()
    const nodes = treeQuery.data?.nodes ?? []
    const root = self
    const nodeById = new Map(nodes.map((node) => [node.id, node]))
    const pathFor = (node: (typeof nodes)[number]): string[] => {
      const path = [node.id]
      const seen = new Set(path)
      let parentId = node.parentId
      while (parentId && !seen.has(parentId)) {
        path.unshift(parentId)
        if (idsEqual(parentId, root?.id)) break
        seen.add(parentId)
        parentId = nodeById.get(parentId)?.parentId ?? null
      }
      return path
    }
    if (root) byId.set(root.id, root)
    for (const node of nodes) {
      if (idsEqual(node.id, root?.id)) continue
      const account: ReportAccount = {
        id: node.id,
        username: node.username ?? node.name,
        displayName: node.name,
        role: levelToRole(node.level),
        parentId: node.parentId,
        path: pathFor(node),
        memberCount: node.level === 6 ? 0 : nodes.filter((n) => idsEqual(n.parentId, node.id) && n.level === 6).length,
      }
      byId.set(node.id, account)
    }
    if (root && nodes.length) {
      const startParent = treeStartParentId(nodes, root.id)
      for (const account of byId.values()) {
        if (account.id === root.id) continue
        const parentKey = account.parentId && byId.has(account.parentId) ? account.parentId : startParent
        if (parentKey == null && account.parentId == null && !idsEqual(account.id, root.id)) {
          const list = children.get(root.id) ?? []
          list.push(account)
          children.set(root.id, list)
          continue
        }
        if (!parentKey || !byId.has(parentKey)) continue
        const list = children.get(parentKey) ?? []
        list.push(account)
        children.set(parentKey, list)
      }
    } else {
      for (const account of byId.values()) {
        if (!account.parentId || !byId.has(account.parentId)) continue
        const list = children.get(account.parentId) ?? []
        list.push(account)
        children.set(account.parentId, list)
      }
    }
    for (const list of children.values()) {
      list.sort((a, b) => a.username.localeCompare(b.username, 'en-IN'))
    }
    return { byId, children }
  }, [self, treeQuery.data?.nodes])

  /**
   * Which tiers the tree is offering. An unselected tier hides its rows, but an
   * agent that is an ANCESTOR of a surviving row stays — a tree that drops the
   * middle of a chain stops being a hierarchy and becomes a list wearing indents.
   */
  const tierFiltered = tiers.length > 0
  const searching = needle.trim().length > 0

  const keep = useMemo(() => {
    if (!self) return new Set<string>()
    if (!tierFiltered && !searching) return null // null = no filter, keep everything
    const text = needle.trim().toLowerCase()
    const hits = new Set<string>()
    for (const account of index.byId.values()) {
      if (account.id === self.id) continue
      if (tierFiltered && !tiers.includes(account.role)) continue
      if (
        text &&
        !account.username.toLowerCase().includes(text) &&
        !account.displayName.toLowerCase().includes(text)
      ) {
        continue
      }
      hits.add(account.id)
      // Ancestors ride along so the surviving rows keep their place in the chain.
      for (const ancestorId of account.path) if (index.byId.has(ancestorId)) hits.add(ancestorId)
    }
    hits.add(self.id)
    return hits
  }, [index, needle, searching, self, tierFiltered, tiers])

  /** The visible rows, top to bottom — the cursor's only source of truth. */
  const rows = useMemo(() => {
    if (!self) return [] as Row[]
    const out: Row[] = []
    const walk = (account: ReportAccount, depth: number, posinset: number, setsize: number) => {
      const kids = (index.children.get(account.id) ?? []).filter((c) => !keep || keep.has(c.id))
      // While a filter is on, every surviving branch is open: an operator who
      // typed three letters should not then have to click open five twisties.
      const isOpen = keep ? true : expanded.has(account.id)
      out.push({
        account,
        depth,
        hasChildren: kids.length > 0,
        expanded: isOpen && kids.length > 0,
        posinset,
        setsize,
      })
      if (isOpen) kids.forEach((child, i) => walk(child, depth + 1, i + 1, kids.length))
    }
    walk(self, 0, 1, 1)
    return out
  }, [expanded, index, keep, self])

  const cursorIndex = Math.max(
    0,
    rows.findIndex((r) => r.account.id === cursor),
  )

  // Focus follows the cursor. Deferred a frame because the Popover's own
  // focus-on-open runs after this component's effects (child effects fire
  // first) and would otherwise pull focus onto the filter box behind us.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      rowRefs.current.get(rows[cursorIndex]?.account.id ?? '')?.focus()
    })
    return () => cancelAnimationFrame(id)
    // Mount only. After this, focus moves because a key moved it — re-running
    // on every `rows` change would yank focus back mid-typing in the filter box.
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const moveTo = (next: number) => {
    const row = rows[Math.max(0, Math.min(rows.length - 1, next))]
    if (!row) return
    setCursor(row.account.id)
    rowRefs.current.get(row.account.id)?.focus()
  }

  const toggle = (id: string, next: boolean) => {
    setExpanded((prev) => {
      const copy = new Set(prev)
      if (next) copy.add(id)
      else copy.delete(id)
      return copy
    })
  }

  const onKeyDown = (event: KeyboardEvent<HTMLUListElement>) => {
    const row = rows[cursorIndex]
    if (!row) return
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        moveTo(cursorIndex + 1)
        return
      case 'ArrowUp':
        event.preventDefault()
        moveTo(cursorIndex - 1)
        return
      case 'Home':
        event.preventDefault()
        moveTo(0)
        return
      case 'End':
        event.preventDefault()
        moveTo(rows.length - 1)
        return
      case 'ArrowRight':
        event.preventDefault()
        if (row.hasChildren && !row.expanded) toggle(row.account.id, true)
        else if (row.expanded) moveTo(cursorIndex + 1)
        return
      case 'ArrowLeft': {
        event.preventDefault()
        if (row.expanded) {
          toggle(row.account.id, false)
          return
        }
        // Walk out to the parent: the nearest row above sitting one level up.
        for (let i = cursorIndex - 1; i >= 0; i--) {
          if (rows[i]!.depth < row.depth) {
            moveTo(i)
            return
          }
        }
        return
      }
      case 'Enter':
      case ' ':
        event.preventDefault()
        onPick(row.account.id === self?.id ? null : row.account.id)
        return
      default:
        break
    }

    // Type-ahead. One printable character, no modifiers — anything else is a
    // shortcut the browser or the popover owns.
    if (event.key.length !== 1 || event.metaKey || event.ctrlKey || event.altKey) return
    const now = Date.now()
    const buffer =
      now - typeahead.current.at > TYPEAHEAD_MS
        ? event.key.toLowerCase()
        : typeahead.current.text + event.key.toLowerCase()
    typeahead.current = { text: buffer, at: now }
    for (let step = 1; step <= rows.length; step++) {
      const candidate = rows[(cursorIndex + step) % rows.length]!
      if (candidate.account.username.toLowerCase().startsWith(buffer)) {
        event.preventDefault()
        moveTo(rows.indexOf(candidate))
        return
      }
    }
  }

  /**
   * Only the tiers that actually exist beneath you.
   *
   * A Double Master's downline can never contain a Triple Master, and a chip
   * offering one is a control that provably does nothing — the exact defect
   * this bar exists to remove. Derived from the loaded set rather than from the
   * role ladder, so it stays right whatever shape a book turns out to be.
   */
  const offeredTiers = useMemo(() => {
    const found = new Set<Role>()
    for (const account of index.byId.values()) {
      if (account.id !== self?.id) found.add(account.role)
    }
    return AGENT_ROLES.filter((role) => found.has(role))
  }, [index, self])

  const crumbs = useMemo<Crumb[]>(() => {
    if (!self) return []
    const picked = value ? index.byId.get(value) : self
    if (!picked || picked.id === self.id) return [{ label: `You · ${self.username}` }]
    const chain = picked.path.slice(picked.path.indexOf(self.id))
    return [
      ...chain.map((id) => ({
        label: index.byId.get(id)?.username ?? id,
        href: id === self.id ? 'self' : id,
      })),
      { label: picked.username },
    ]
  }, [index, self, value])

  return (
    <>
      <p className="w7-scopelede">
        Show this report as if you were signed in as anyone beneath you. Figures are re-read for
        their seat — never re-split from a percentage.
      </p>

      {crumbs.length > 1 ? (
        <Breadcrumb items={crumbs} onNavigate={(href) => onPick(href === 'self' ? null : href)} />
      ) : null}

      <SearchInput
        value={needle}
        onChange={setNeedle}
        label="Filter your downline"
        placeholder="Filter your downline"
      />

      {offeredTiers.length > 1 ? (
        <div className="w7-chiprow">
          {offeredTiers.map((role) => (
            <FilterChip
              key={role}
              active={tiers.includes(role)}
              onClick={() =>
                setTiers((prev) =>
                  prev.includes(role)
                    ? prev.filter((r) => r !== role)
                    : [...prev, role].sort((a, b) => roleDepth(a) - roleDepth(b)),
                )
              }
            >
              {ROLE_LABEL[role]}
            </FilterChip>
          ))}
        </div>
      ) : null}

      {isLoading && rows.length <= 1 ? (
        <span className="w7-row" style={{ gap: 'var(--w7-s2)' }}>
          <Spinner size={14} />
          <span className="w7-dim" style={{ fontSize: 12 }}>
            Loading your downline
          </span>
        </span>
      ) : null}

      <ul className="w7-scopetree" role="tree" aria-label="Your downline" onKeyDown={onKeyDown}>
        {rows.map((row, i) => {
          const selected = row.depth === 0 ? value === null : value === row.account.id
          return (
            <li
              key={row.account.id}
              ref={(el) => {
                if (el) rowRefs.current.set(row.account.id, el)
                else rowRefs.current.delete(row.account.id)
              }}
              role="treeitem"
              className="w7-scoperow"
              aria-level={row.depth + 1}
              aria-posinset={row.posinset}
              aria-setsize={row.setsize}
              aria-selected={selected}
              aria-expanded={row.hasChildren ? row.expanded : undefined}
              tabIndex={i === cursorIndex ? 0 : -1}
              style={{ paddingLeft: `calc(var(--w7-s2) + ${row.depth} * var(--w7-s5))` }}
              onFocus={() => setCursor(row.account.id)}
              onClick={() => onPick(row.depth === 0 ? null : row.account.id)}
            >
              {/* Mouse-only: the twisty is aria-hidden and unfocusable because
                  ArrowRight/ArrowLeft already own expansion for the keyboard,
                  and a second tab stop per row would treble the tree's depth. */}
              <span
                className="w7-scoperow-twisty"
                data-empty={row.hasChildren ? undefined : 'true'}
                aria-hidden="true"
                onClick={(event) => {
                  event.stopPropagation()
                  if (row.hasChildren && !keep) toggle(row.account.id, !row.expanded)
                }}
              >
                <Icon name={row.expanded ? 'chevron-down' : 'chevron-right'} size={14} />
              </span>

              <span className="w7-scoperow-name">
                {row.depth === 0 ? `You · ${row.account.username}` : row.account.username}
                <span className="w7-scoperow-sub">{row.account.displayName}</span>
              </span>

              <RoleChip role={row.account.role} label={ROLE_LABEL[row.account.role]} />
              <span className="w7-scoperow-count">{count(row.account.memberCount)} members</span>
            </li>
          )
        })}
      </ul>

      <p className="w7-scopenote">
        Looking for one member’s figures? Use the member filter, or open their page.
      </p>
    </>
  )
}
